import type { Repeat } from '@bayer-int/portletce-shared';
import {
    VegaSpec,
    PipeDocument,
    Signal,
    PlainRepeat,
    SignalReference,
    createNamedStringCodec,
    nullableToArray,
} from '@bayer-int/portletce-shared';
import type { Specification } from '@bayer-int/portletce-aclibv2';
import * as t from 'io-ts';
import { isNull, isUndefined, mapValues, omitBy } from 'lodash-es';

import { isNotNullable } from 'portlet/common/utils';

import type { FilterData } from './filters';
import { Settings } from './settings';
import { ListenTo, type ListenToDefinitions } from './listenTo';
import { PortletQueryC } from './query';
import { ViewHelper } from './viewHelper';

export type ViewId = number;
export type AcSpecId = string;
export type AcSpecification = VegaSpec;
export type AcSpecOrId = AcSpecId | AcSpecIdWithScopedProperties | VegaSpec;
export type AcPipeDocument = PipeDocument;
export type AcPipeDocOrId = AcSpecId | AcPipeDocIdWithScopedProperties | AcPipeDocument;
export type AcSpecIdWithScopedProperties =
    | t.TypeOf<typeof AcSourceSpecIdWithScopedProperties>
    | t.TypeOf<typeof AcTransformSpecIdWithScopedProperties>;
export type AcPipeDocIdWithScopedProperties = t.TypeOf<typeof AcPipeDocIdWithScopedProperties>;

const ViewId = t.number;

export const UtilitySpecIdC = createNamedStringCodec('UtilitySpecId');
export const AcSourceSpecIdC = createNamedStringCodec('AcSourceSpecId');
export const AcTransformSpecIdC = createNamedStringCodec('AcTransformSpecId');
export const AcDocSpecIdC = createNamedStringCodec('AcDocumentSpecId');

const AcSourceSpecIdWithScopedProperties = t.intersection([
    t.type({
        name: AcSourceSpecIdC,
        props: t.record(t.string, t.union([t.unknown, SignalReference.codec])),
    }),
    t.partial({
        spec: VegaSpec.codec,
        importMapping: t.record(t.string, t.string),
        exportMapping: t.record(t.string, t.string),
        version: t.number,
    }),
]);

const UtilitySpecIdWithScopedProperties = t.intersection([
    t.type({
        name: UtilitySpecIdC,
        props: t.record(t.string, t.union([t.unknown, SignalReference.codec])),
    }),
    t.partial({
        spec: VegaSpec.codec,
        importMapping: t.record(t.string, t.string),
        exportMapping: t.record(t.string, t.string),
        version: t.number,
    }),
]);

const AcTransformSpecIdWithScopedProperties = t.intersection([
    t.type({
        name: AcTransformSpecIdC,
        props: t.record(t.string, t.union([t.unknown, SignalReference.codec])),
    }),
    t.partial({
        spec: VegaSpec.codec,
        importMapping: t.record(t.string, t.string),
        exportMapping: t.record(t.string, t.string),
        version: t.number,
    }),
]);

const AcPipeDocIdWithScopedProperties = t.intersection([
    t.type({
        name: AcDocSpecIdC,
        props: t.record(t.string, t.union([t.unknown, SignalReference.codec])),
    }),
    t.partial({
        spec: PipeDocument.codec,
        importMapping: t.record(t.string, t.string),
        exportMapping: t.record(t.string, t.string),
        version: t.number,
    }),
]);

/**
 * Common part of required properties of above codecs. Does not perform validation of optional fields, like `spec`.
 */
const requiredSpecIdWithScopedProperties: t.Type<
    t.TypeOf<
        | typeof AcSourceSpecIdWithScopedProperties
        | typeof UtilitySpecIdWithScopedProperties
        | typeof AcTransformSpecIdWithScopedProperties
        | typeof AcPipeDocIdWithScopedProperties
    >
> = t.intersection([
    t.type({
        name: t.union([AcSourceSpecIdC, UtilitySpecIdC, AcTransformSpecIdC, AcDocSpecIdC]),
        props: t.record(t.string, t.union([t.unknown, SignalReference.codec])),
    }),
    t.partial({
        version: t.number,
    }),
]);

const UtilitySpecOrIdC = t.union([
    UtilitySpecIdC,
    UtilitySpecIdWithScopedProperties,
    VegaSpec.codec,
]);

const VegaSourceSpecOrIdC = t.union([
    AcSourceSpecIdC,
    AcSourceSpecIdWithScopedProperties,
    t.array(t.union([AcSourceSpecIdC, AcSourceSpecIdWithScopedProperties, VegaSpec.codec])),
    VegaSpec.codec,
]);

const VegaTransformSpecOrIdC = t.union([
    AcTransformSpecIdC,
    AcTransformSpecIdWithScopedProperties,
    VegaSpec.codec,
]);

const PipeDocOrIdC = t.union([AcDocSpecIdC, AcPipeDocIdWithScopedProperties, PipeDocument.codec]);

const ViewRequiredC = t.type({
    id: ViewId,
    label: t.string,
    datapipe: t.type({
        source: VegaSourceSpecOrIdC,
        transforms: t.array(VegaTransformSpecOrIdC),
        document: PipeDocOrIdC,
    }),
});

const ViewOptionalC = t.partial({
    listenTo: t.array(ListenTo.codec),
    signals: t.array(Signal.codec),
    repeats: t.array(PlainRepeat.codec),
    utilities: t.array(UtilitySpecOrIdC),
    settings: Settings.codec,
    signalToDownload: t.union([
        t.string,
        t.type({
            signalName: t.string,
            downloadFormat: t.union([t.literal('csv'), t.literal('xlsx')]),
        }),
    ]),
    customErrorMessages: t.record(t.string, t.type({ title: t.string, message: t.string })),
    query: PortletQueryC,
    smartViewTags: t.record(t.string, t.string),
    viewGroup: t.intersection([
        t.type({
            form: t.string,
            name: t.string,
        }),
        t.partial({
            default: t.boolean,
        }),
    ]),
});

const ViewC = t.intersection([ViewRequiredC, ViewOptionalC], 'View');
const ViewWithReferenceC = t.intersection(
    [
        t.partial(ViewRequiredC.props),
        ViewOptionalC,
        t.type({ id: ViewId, label: t.string, $ref: ViewId }),
    ],
    'ViewWithReference',
);

export type View = t.TypeOf<typeof ViewC>;
export type ViewWithReference = t.TypeOf<typeof ViewWithReferenceC>;

export type ChartRenderProps = {
    viewId: ViewId;
    specs: Specification[];
    utilities: Specification[];
    documentSignals: Signal[];
    documentRepeats: Repeat[];
    documentImportMapping: Record<string, string>;
    document: AcPipeDocument;
    /**
     * trigger vega loader cache invalidation by updating chart dependency
     * determine if chart should invalidate cache
     * by comparing latest cache renderProp with chart cache timestamp
     * to trigger vega dfe pipe create with non caching data loader
     */
    cacheInvalidationTimestamp: string | null;
    /**
     * If set to true, portlet should not update the visualization.
     * When toggled back to false, the view should be rerendered.
     */
    isSuspended: boolean;
    /** if set to true, aclib should skip all transitions and animations. useful in visual testing */
    disableAnimations: boolean;
    filterKeyToEmitFrom: string | null;
};

export const View = {
    codec: ViewC,
    withRefCodec: ViewWithReferenceC,
    viewRequiredCodec: ViewRequiredC,
    viewOptionalC: ViewOptionalC,
    specIdWithScopedProperties: t.union([
        UtilitySpecIdWithScopedProperties,
        AcSourceSpecIdWithScopedProperties,
        AcTransformSpecIdWithScopedProperties,
    ]),
    docIdWithScopedProperties: AcPipeDocIdWithScopedProperties,
    requiredSpecIdWithScopedProperties,

    DEFAULT_VIEW_ID: 1,
    INVALID_VIEW_ID: -1,

    default: (): View => ({
        id: View.DEFAULT_VIEW_ID,
        label: 'default view',
        datapipe: {
            source: 'nil-source',
            transforms: [],
            document: 'empty-aclib-document',
        },
    }),

    getObservedFilters: (view: View): ListenToDefinitions => view.listenTo || [],

    getDefaultFilters: (_view: View): FilterData[] => [],

    getSignalToDownaload: (
        view: View,
    ): string | { signalName: string; downloadFormat: 'csv' | 'xlsx' } =>
        view.signalToDownload || '',

    getCustomErrorMessages: (view: View): Record<string, { title: string; message: string }> =>
        view.customErrorMessages || {},

    getReferencedSpecIds: ({
        source,
        transforms,
        document,
    }: View['datapipe']): Array<{ name: AcSpecId; version: number | null }> => {
        const sourceIds = nullableToArray(source).map(source =>
            t.string.is(source)
                ? { name: source, version: null }
                : View.specIdWithScopedProperties.is(source)
                  ? { name: source.name, version: source.version || null }
                  : null,
        );

        const transformIds = transforms.map(transform =>
            t.string.is(transform)
                ? { name: transform, version: null }
                : View.specIdWithScopedProperties.is(transform)
                  ? { name: transform.name, version: transform.version || null }
                  : null,
        );

        const documentId = t.string.is(document)
            ? { name: document, version: null }
            : View.docIdWithScopedProperties.is(document)
              ? { name: document.name, version: document.version || null }
              : null;

        return [...sourceIds, ...transformIds, documentId].filter(isNotNullable);
    },

    resolveDatapipeSpecReferences: (
        datapipe: View['datapipe'],
        replaceSpecId: (id: AcSpecId) => AcSpecification | undefined,
        replaceDocId: (id: AcSpecId) => AcPipeDocument | undefined,
        latestSpecVersions: Array<{ id: AcSpecId; latestVersion: number }>,
    ): View['datapipe'] => {
        const { source: sourceId, transforms: transformsIds, document: documentId } = datapipe;

        const createSpecUniqKey = (
            spec: t.TypeOf<typeof requiredSpecIdWithScopedProperties> | string,
        ) => {
            if (t.string.is(spec)) {
                const latestSpecVersion = latestSpecVersions.find(
                    ({ id }) => id === spec,
                )?.latestVersion;
                if (!latestSpecVersion) return spec.toLowerCase();

                return `${spec.toLowerCase()}-v${latestSpecVersion}`;
            }
            if (!spec.version) {
                const latestSpecVersion = latestSpecVersions.find(
                    ({ id }) => id === spec.name,
                )?.latestVersion;
                if (!latestSpecVersion) return spec.name.toLowerCase();
                return `${spec.name.toLowerCase()}-v${latestSpecVersion}`;
            }
            return `${spec.name.toLowerCase()}-v${spec.version}`;
        };

        const source: View['datapipe']['source'] = t.string.is(sourceId)
            ? replaceSpecId(createSpecUniqKey(sourceId)) || sourceId
            : View.specIdWithScopedProperties.is(sourceId)
              ? { ...sourceId, spec: replaceSpecId(createSpecUniqKey(sourceId.name)) }
              : sourceId;

        const transforms: View['datapipe']['transforms'] = transformsIds.map(transformId =>
            t.string.is(transformId)
                ? replaceSpecId(createSpecUniqKey(transformId)) || transformId
                : View.specIdWithScopedProperties.is(transformId)
                  ? { ...transformId, spec: replaceSpecId(createSpecUniqKey(transformId.name)) }
                  : transformId,
        );

        const document: View['datapipe']['document'] = t.string.is(documentId)
            ? replaceDocId(createSpecUniqKey(documentId)) || documentId
            : View.docIdWithScopedProperties.is(documentId)
              ? { ...documentId, spec: replaceDocId(createSpecUniqKey(documentId.name)) }
              : documentId;

        return { source, transforms, document };
    },

    merge: (originalView: ViewWithReference, referencedView: View): View =>
        ViewHelper.merge(originalView, referencedView),

    isReferenceView: (view: View | ViewWithReference): view is ViewWithReference =>
        ViewHelper.isReferenceView(view),

    toReplacementTags: (signals: Signal[]): Record<string, string> => {
        const filteredSignals = signals.filter(signal => !isUndefined(signal.value));

        const replacement = filteredSignals.reduce((obj, item) => {
            return Object.assign(obj, {
                [item.name]: item.value,
            });
        }, {});

        return mapValues(omitBy(replacement, isNull), value => String(value));
    },
};

export type ViewGroupMember = { id: ViewId; label: string; form: string };

/*
 ** `null` in ViewGroup.name indicates a view without a viewGroup
 ** That's how we distinguish them from single view groups
 */
export type ViewGroup = {
    name: string | null;
    members: Array<ViewGroupMember>;
    defaultView: ViewId;
};

export type ViewGroups = Array<ViewGroup>;
